import {AdManager} from './ad.manager';
import {singleton} from "../../../infrastructure/decorators/singleton";
import {makeObservable, observable} from "mobx";
import type {DomUtils} from "../../../infrastructure/utils/dom-utils";
import type {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import type {SiteConfig} from "../../bootstrap/models/site-config";
import type {DeviceStore} from "../../app/device.store";

@singleton
export class GoogleAdManager extends AdManager {
  @observable isInitialized: Boolean = false;

  static injectables = [...AdManager.injectables];

  constructor(domUtils: DomUtils, logger: LoggerUtils, siteConfig: SiteConfig, deviceStore: DeviceStore) {
    super(domUtils, logger, siteConfig, deviceStore);

    this.provider = window.googletag;
    this.activeSlots = new Map();
    this.isInitialized = false;
    makeObservable(this);

    this.callbacks = [];
    window.googletag = window.googletag || {cmd: []};

    this.enableServices();
  }

  initializeUnit(adUnitModel) {
    window.googletag.cmd.push(() => {
      if (typeof window.googletag === 'undefined') return;

      const {adUnit, width, height, slotId, sizes} = adUnitModel;

      if (this.activeSlots.has(slotId)) return;

      try {
        const fitSizes = sizes?.filter((size) => (size[0] < window.innerWidth) && (size[1] < window.innerHeight));
        const adSlot = window.googletag.defineSlot(adUnit, fitSizes || [width, height], slotId).addService(window.googletag.pubads());

        window.googletag.display(slotId);

        this.activeSlots.set(slotId, adSlot);
        console.log(`GAM: Displayed slot: ${slotId}`);
      } catch (error) {
        console.error(`GAM: Error displaying slot ${slotId}:`, error);
      }
    });
  }

  destroyUnits(units) {
    window.googletag.cmd.push(() => this.handleDestroyUnits(units));
  }

  refreshUnits() {
    console.warn('GAM: Refresh is not recommended for interstitial slots.');
  }

  destroyAll() {
    window.googletag.cmd.push(() => this.handleDestroyUnits(
      Array.from(this.activeSlots.keys())
    ));
  }

  enableServices() {
    if (typeof window.googletag === 'undefined') return;

    window.googletag.cmd.push(() => {
      if (!window.googletag.pubads().get('enableServices')) {
        window.googletag.pubads().enableSingleRequest();
        window.googletag.enableServices();
        this.handleIsReady()

        if (typeof window.googletag.setConfig === 'function') {
          window.googletag.setConfig({ targeting: { has_hb: "off" } });
        }
      }
    });
  }

  handleIsReady = () => {
    this.isInitialized = true;
    this.callbacks.map(({callback}) => callback(this.isInitialized));
  }

  handleDestroyUnits(slotIds) {
    if (typeof window.googletag === 'undefined') return;

    const slotsToDestroy = [];

    slotIds.forEach(slotId => {
      const slot = this.activeSlots.get(slotId);
      if (slot) {
        slotsToDestroy.push(slot);
        this.activeSlots.delete(slotId);
      }
    });

    if (slotsToDestroy.length > 0) {
      window.googletag.destroySlots(slotsToDestroy);
      console.log(`GAM: Destroyed slots: ${slotIds.join(', ')}`);
    }
  }
}